import { CheckCircleIcon, ClockCountdownIcon } from '@phosphor-icons/react';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo } from 'react';

import {
  DataTable,
  makeListRequest,
  personCell,
  useTableMemoryState,
} from '@/components/data/data-table';

import { getApplicableEntities } from '../api';
import { REDEMPTION_STATUS_LABEL } from '../format';
import type { CustomerEntity, RedemptionStatus as RedemptionStatusType } from '../types';

import styles from './discount-customers-tab.module.scss';

/** Per-Customer redemption state — inline icon + text (NOT a Chip/pill; ticket 06 Figma). */
function RedemptionStatus({ status }: { status: RedemptionStatusType }) {
  const used = status === 'used';
  const Icon = used ? CheckCircleIcon : ClockCountdownIcon;
  return (
    <span className={`${styles.status} ${used ? styles.used : styles.notUsed}`}>
      <Icon size={16} weight="fill" aria-hidden />
      {REDEMPTION_STATUS_LABEL[status]}
    </span>
  );
}

export type DiscountCustomersTabProps = {
  discountId: string;
  search: string;
};

/** Customers relationship tab — reuses the shared `<DataTable>` (its chrome is Figma-correct as-is)
 *  over the discount's applicable customers, filtered by the header search. Unlike the card-grid
 *  tabs (which use `useApplicableEntities`), the table drives its own fetch through `<DataTable>`'s
 *  `request` prop + internal query key (`discount-<id>-customers`) — the two share the same api fn
 *  but not the same query key. */
export function DiscountCustomersTab({ discountId, search }: DiscountCustomersTabProps) {
  const table = useTableMemoryState(10);
  const { setState } = table;

  useEffect(() => {
    setState({ page: 1 });
  }, [search, setState]);

  const request = useMemo(
    () =>
      makeListRequest<CustomerEntity, { search?: string }>((p) =>
        getApplicableEntities(discountId, 'customer', p),
      ),
    [discountId],
  );

  // Widths from Figma (INT - HFD - Greenway `z3ptvnEQi4u7P2NsTTUWfA`, frame 4083:219055) — ADR-0032.
  // Every column declares one, so `<DataTable>` lays out fixed and a long Customer name truncates
  // inside its column instead of widening it and pushing Phone Number and Status off-screen. All
  // three take the frame's header widths verbatim (its hidden "Discount used on" column is not built).
  const columns: ColumnsType<CustomerEntity> = [
    {
      title: 'Customer',
      dataIndex: 'name',
      sorter: true,
      width: 260,
      render: (name: string) => personCell(name),
    },
    { title: 'Phone Number', dataIndex: 'phone', sorter: true, width: 255 },
    {
      title: 'Status',
      dataIndex: 'status',
      sorter: true,
      width: 186,
      render: (status: RedemptionStatusType) => <RedemptionStatus status={status} />,
    },
  ];

  return (
    <DataTable<CustomerEntity, { search?: string }>
      stateStore={table}
      tableId={`discount-${discountId}-customers`}
      columns={columns}
      request={request}
      filters={{ search }}
      rowKey="id"
      defaultPageSize={10}
      pageSizeOptions={[10, 20, 30, 40, 50]}
      hideReload
      emptyText="No customers found"
    />
  );
}
